import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ContractorAuthProvider, useContractorAuth } from "./context/ContractorAuthContext";
import ContractorLogin from "./pages/ContractorLogin";
import ContractorPortal from "./pages/ContractorPortal";
import ContractorPermitView from "./pages/ContractorPermitView";
import ResetPassword from "./pages/ResetPassword";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AddContractor from "./pages/AddContractor";
import AddInduction from "./pages/AddInduction";
import ContractorProfile from "./pages/ContractorProfile";
import IssuePermit from "./pages/IssuePermit";
import PermitDetail from "./pages/PermitDetail";
import PublicVerify from "./pages/PublicVerify";
import AdminUsers from "./pages/AdminUsers";
import AuditLog from "./pages/AuditLog";
import AdminApprovals from "./pages/AdminApprovals";
import AuthCallback from "./pages/AuthCallback";
import PendingApproval from "./pages/PendingApproval";
import SelfInduct from "./pages/SelfInduct";
import InductionRequests from "./pages/InductionRequests";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (user.status === "pending") return <Navigate to="/pending" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ContractorRoute({ children }) {
  const { data } = useContractorAuth();
  const loc = useLocation();
  if (data === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  if (data === false)
    return <Navigate to={`/contractor/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ContractorAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/verify/:id" element={<PublicVerify />} />
              <Route path="/contractor/login" element={<ContractorLogin />} />
              <Route path="/contractor/reset-password" element={<ResetPassword />} />
              <Route path="/contractor" element={<ContractorRoute><ContractorPortal /></ContractorRoute>} />
              <Route path="/contractor/permits/:id" element={<ContractorRoute><ContractorPermitView /></ContractorRoute>} />
              <Route path="/self-induct" element={<ContractorRoute><SelfInduct /></ContractorRoute>} />
              <Route path="/self-induct/:site" element={<ContractorRoute><SelfInduct /></ContractorRoute>} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/contractors/new" element={<ProtectedRoute><AddContractor /></ProtectedRoute>} />
              <Route path="/contractors/:id" element={<ProtectedRoute><ContractorProfile /></ProtectedRoute>} />
              <Route path="/contractors/:id/induct" element={<ProtectedRoute><AddInduction /></ProtectedRoute>} />
              <Route path="/contractors/:id/issue" element={<ProtectedRoute><IssuePermit /></ProtectedRoute>} />
              <Route path="/permits/:id" element={<ProtectedRoute><PermitDetail /></ProtectedRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/audit" element={<AdminRoute><AuditLog /></AdminRoute>} />
              <Route path="/inductions" element={<ProtectedRoute><InductionRequests /></ProtectedRoute>} />
              <Route path="/admin/approvals" element={<AdminRoute><AdminApprovals /></AdminRoute>} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/pending" element={<PendingApproval />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </ContractorAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
